import React from "react";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import IMAGES from "@/public/assets/img";
import { glossaryBreadcrumbSchema } from "@/lib/glossaryBreadcrumSchema";

const Glossary = ({ post, relatedPosts }) => {
  const router = useRouter();
 const breadcrumbSchema = glossaryBreadcrumbSchema({
    homeUrl: WEB_URL,
    glossaryURL: post.slug,
    glossaryTitle: post.title,
  });
  
  return (
    <main className="pt-[115px]">
      <Head>
      <title>{`${post.title} Meaning, Working and Benefits | Finance Glossary`}</title>
        <meta
          name="description"
          content={`Definition and meaning of ${post.title}. Understand its working and benefits with examples.`}
        />
        {/* OpenGraph tags */}
        <meta
          property="og:title"
          content={`${post.title} Meaning, Working & Benefits | Finance Glossary`}
        />
        <meta
          property="og:description"
          content={`Definition and meaning of ${post.title}. Understand its working and benefits with examples.`}
        />
        <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
        <meta property="og:url" content={WEB_URL + router.asPath} />
        <link rel="canonical" href={WEB_URL + router.asPath} />
        <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema)
        }}
      />
      </Head>
      <section>
        <div className="relative sm:z-[10] bg-no-repeat sm:bg-center bg-cover bg-bottom py-6">
          <Image
            priority
            src={IMAGES.ImportFactoring.ImportBGBanner.src}
            width={1366}
            height={550}
            className="absolute inset-0 max-w-none w-full h-full -z-[1]"
            alt="Banner Desktop"
          />

          <div className="container px-3">
            <p className="text-center text-heading5 md:text-heading3 sm:text-lg font-medium text-white">
              {post.title}
            </p>
          </div>
        </div>
        <div className="container px-3 my-6">
          <p className="text-sm text-secondary mb-3">
            <Link href={WEB_URL} rel="noopener noreferrer">
              <span className="text-[#2369d8] cursor-pointer font-sm">
                {" "}
                Home {"  "}
              </span>
            </Link>
            {"  "}
            {" > "}
            <Link
              href={WEB_URL + `/finance-glossary`}
              rel="noopener noreferrer"
            >
              <span className="text-[#2369d8] cursor-pointer font-sm">
                {" "}
                Finance Glossary
              </span>
            </Link>
            {"  "}
            {" > "}
            <span className="font-medium">{post.title}</span>
          </p>

          <div className="my-4">
            <h1 className="text-heading5 sm:text-textxl text-secondary font-medium mb-2">
              {`What is ${post.title}?`}
            </h1>
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {relatedPosts.length > 1 && (
            <>
            <div className="bg-[#F5F9FC] px-4 py-6 rounded-xl mt-6 sm:py-4 sm:px-3">
              <h3 className="font-medium text-lg mb-4">
               {` Related Terms Starting with "${relatedPosts[0].category}"` }
              </h3>
              <ul className="flex flex-wrap gap-1">
                {relatedPosts.filter((term) => term.slug !== post.slug)
                .map((term, index) => (
                  <li
                    key={index}
                    className="relative text-base border border-bordercolor py-1 px-2 bg-white rounded-md hover:border-brand"
                  >
                    <Link
                      className="text-secondary hover:text-brand"
                      href={`${WEB_URL}/finance-glossary/${term.slug}`}
                    >
                      {term.title}
                    </Link>
                  </li>
                ))}
              </ul>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default Glossary;

export async function getServerSideProps(context) {
  const { params } = context;
  const slug = params.glossary;

  let post = null;
  let relatedPosts = [];

  try {
    // Fetch main glossary detail
    const apiRes = await fetch(ENDPOINTS.GLOSSARY_API_DETAIL(slug));
    if (!apiRes.ok) {
      console.error(`API returned status ${apiRes.status}`);
      return { notFound: true };
    }

    const glossaryDetail = await apiRes.json();
    const category = glossaryDetail.data.categories[0];
    post = {
      title: glossaryDetail.data.title,
      slug: glossaryDetail.data.slug,
      content: glossaryDetail.data.content,
      category: category.slug,
      parentCategory: category.parentCategory,
    };

    // Fetch related posts
    if (category.parentCategory && category.slug) {
      const relatedRes = await fetch(
        ENDPOINTS.RELATED_GLOSSARY_POST(category.parentCategory, category.slug)
      );
      const relatedData = await relatedRes.json();
      relatedPosts = (relatedData.data.content || []).map((item) => ({
        slug: item.slug,
        title: item.title,
        category: category.name,
      }));
    }
  } catch (err) {
    console.error("Error fetching glossary data:", err);
    return { notFound: true };
  }

  return {
    props: {
      post,
      relatedPosts,
    },
  };
}
